import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig, CanceledError } from 'axios';
import { getAuthSession, onAuthSessionChange } from '../lib/authSession';
import { ApiResponse } from '../types';

// Base API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const PUBLIC_API_URL = import.meta.env.VITE_PUBLIC_URL || '/public';

// Create axios instance with timeout
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000, // 15 seconds timeout to prevent hanging requests
  headers: {
    'Content-Type': 'application/json',
  },
});

// Create public axios instance with timeout
export const publicApiClient: AxiosInstance = axios.create({
  baseURL: PUBLIC_API_URL,
  timeout: 15000, // 15 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// FE-K04: Helper to read persisted auth state from Zustand's sessionStorage key
// Dies ist die Single Source of Truth — keine separaten sessionStorage-Keys mehr
interface PersistedAuthState {
  token?: string | null;
  refreshToken?: string | null;
  tenant?: { id: string; key: string } | null;
}

function getPersistedAuthState(): PersistedAuthState {
  try {
    const raw = sessionStorage.getItem('auth-storage');
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return (parsed?.state as PersistedAuthState) || {};
  } catch {
    return {};
  }
}

// Request interceptor to add auth token
// FE-K04: Liest aus dem Zustand-persist-Key statt individueller sessionStorage-Keys
apiClient.interceptors.request.use(
  (config) => {
    const session = getAuthSession();
    (config as RetryableRequestConfig)._authRevision = session.revision;
    config.signal = config.signal ? AbortSignal.any([config.signal as AbortSignal, session.signal]) : session.signal;
    const authState = getPersistedAuthState();

    if (authState.token) {
      config.headers.Authorization = `Bearer ${authState.token}`;
    }

    // Add tenant headers if available
    if (authState.tenant?.id) {
      config.headers['X-Tenant-ID'] = authState.tenant.id;
    }
    if (authState.tenant?.key) {
      config.headers['X-Tenant-Key'] = authState.tenant.key;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// FE-K02 / RISK-10 / AUTH-RACE-FIX:
// Single-Flight Pattern — es läuft zu jedem Zeitpunkt höchstens EIN Refresh.
// Alle parallelen 401-Requests warten auf dasselbe Promise und erhalten dann
// den gleichen neuen Access Token. Verhindert die beobachtete Race-Condition,
// bei der mehrere Refresh-Calls parallel den (rotierten) Refresh Token invalidieren.
let refreshPromise: Promise<string> | null = null;

// Einmaliger Logout-Trigger, falls mehrere Refreshs parallel fehlschlagen
// (z. B. wenn der Refresh Token serverseitig revoked ist).
let logoutInFlight = false;
onAuthSessionChange(() => {
  refreshPromise = null;
  logoutInFlight = false;
});
function triggerLogoutOnce(): void {
  if (logoutInFlight) return;
  logoutInFlight = true;
  clearAuthAndRedirect();
}

// Extended config type to track retry flag without `any`
interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
  _authRevision?: number;
}

/**
 * Schleifen-Schutz: Auth-Endpunkte dürfen nicht selbst durch den Refresh-Interceptor laufen.
 * - /auth/refresh: würde sich sonst rekursiv selbst refreshen.
 * - /auth/login:   Falsche Credentials liefern 401, das ist kein Fall für Refresh.
 * - /auth/logout:  Logout nutzt zwar direktes `axios` (bypasst Interceptor), sicherheitshalber gelistet.
 */
function isAuthEndpointExemptFromRefresh(config?: InternalAxiosRequestConfig): boolean {
  const url = config?.url;
  if (!url) return false;
  return (
    url.includes('/auth/refresh') ||
    url.includes('/auth/login') ||
    url.includes('/auth/logout')
  );
}

/**
 * [auth-refresh] Führt den eigentlichen Refresh-Request durch.
 * Bewusst direkter `axios`-Call (nicht `apiClient`), damit der Call weder
 * den Request-Interceptor (der sonst den Access Token setzen würde) noch
 * den Response-Interceptor (Rekursion) durchläuft.
 *
 * Aktualisiert sessionStorage (persist-Key) SYNCHRON, damit Folgerequests
 * im Request-Interceptor sofort den neuen Access Token lesen — die
 * Zustand-`persist`-Middleware schreibt asynchron und wäre zu spät.
 */
async function performRefresh(refreshToken: string): Promise<string> {
  const session = getAuthSession();
  const response = await axios.post<{ access_token: string; refresh_token?: string }>(
    `${API_BASE_URL}/auth/refresh`,
    {},
    {
      headers: { Authorization: `Bearer ${refreshToken}` },
      timeout: 15000,
      signal: session.signal,
    }
  );

  const { access_token, refresh_token: new_refresh_token } = response.data;
  if (session.revision !== getAuthSession().revision) throw new CanceledError('Anmeldung wurde gewechselt');

  // 1) Sync: sessionStorage-persist-Key sofort aktualisieren (Single Source of Truth).
  try {
    const raw = sessionStorage.getItem('auth-storage') || '{"state":{},"version":0}';
    const parsed = JSON.parse(raw);
    parsed.state = {
      ...parsed.state,
      token: access_token,
      ...(new_refresh_token ? { refreshToken: new_refresh_token } : {}),
    };
    sessionStorage.setItem('auth-storage', JSON.stringify(parsed));
  } catch (storageErr) {
    // Nicht fatal — der Retry setzt den Authorization-Header explizit.
    console.warn('[auth-refresh] sessionStorage sync failed:', storageErr);
  }

  // 2) Async: In-Memory Zustand-Store synchronisieren (via dynamic import,
  //    vermeidet Circular-Dep zwischen api.ts ↔ authStore.ts).
  void import('../store/authStore')
    .then(({ useAuthStore }) => {
      if (session.revision !== getAuthSession().revision) return;
      useAuthStore.getState().setTokens(
        access_token,
        new_refresh_token || useAuthStore.getState().refreshToken || ''
      );
    })
    .catch((err) => {
      console.warn('[auth-refresh] auth store sync failed:', err);
    });

  return access_token;
}

/**
 * Single-Flight Wrapper: alle Aufrufer, die während eines laufenden Refresh
 * ankommen, erhalten dasselbe Promise zurück. Nach Abschluss (egal ob erfolgreich
 * oder nicht) wird `refreshPromise` auf `null` zurückgesetzt.
 */
function getOrStartRefresh(refreshToken: string): Promise<string> {
  if (refreshPromise) {
    console.debug('[auth-refresh] joining in-flight refresh');
    return refreshPromise;
  }
  console.debug('[auth-refresh] starting refresh');
  const pending = performRefresh(refreshToken).finally(() => {
    if (refreshPromise === pending) refreshPromise = null;
  });
  refreshPromise = pending;
  return refreshPromise;
}

// Response interceptor for error handling (Single-Flight Refresh)
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if ((response.config as RetryableRequestConfig)._authRevision !== getAuthSession().revision) {
      return Promise.reject(new CanceledError('Anmeldung wurde gewechselt'));
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (!originalRequest) {
      return Promise.reject(error);
    }
    if (originalRequest._authRevision !== getAuthSession().revision) {
      return Promise.reject(new CanceledError('Anmeldung wurde gewechselt'));
    }

    // Schleifen-Schutz: /auth/refresh (und verwandte Auth-Endpunkte)
    // dürfen nicht in den Refresh-Flow geraten.
    if (isAuthEndpointExemptFromRefresh(originalRequest)) {
      return Promise.reject(error);
    }

    // Nur 401 ist ein Refresh-Trigger — und auch nur einmal pro Request.
    if (error.response?.status !== 401 || originalRequest._retry) {
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    // FE-K04: Refresh Token aus persist-Key lesen
    const refreshToken = getPersistedAuthState().refreshToken;
    if (!refreshToken) {
      // Edge Case: kein Refresh Token (z. B. frischer Reload mit leerem Storage)
      // → direkt ausloggen/redirecten, nicht refreshen.
      console.debug('[auth-refresh] no refresh token → logout');
      triggerLogoutOnce();
      return Promise.reject(error);
    }

    try {
      // Single-Flight: parallele 401s warten hier auf dasselbe Promise.
      const newAccessToken = await getOrStartRefresh(refreshToken);

      // Original-Request mit dem neuen Token retryen.
      // Der Request-Interceptor überschreibt Authorization mit dem Wert aus
      // sessionStorage — wir haben diesen oben synchron aktualisiert.
      // Zusätzlich setzen wir den Header hier explizit als Gürtel-und-Hosenträger.
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return apiClient(originalRequest);
    } catch (refreshError) {
      // Refresh selbst fehlgeschlagen:
      // - 401 → Refresh Token abgelaufen/revoked → Logout
      // - Netzwerkfehler → ebenfalls propagieren, aber Logout nur bei 401-Hinweis
      const refreshAxiosErr = refreshError as AxiosError | undefined;
      const refreshStatus = refreshAxiosErr?.response?.status;
      if (refreshStatus === 401 || refreshStatus === 403 || refreshStatus === 422) {
        triggerLogoutOnce();
      } else {
        // Netzwerkfehler / 5xx: Fehler an Caller propagieren, NICHT ausloggen —
        // der User hat ggf. nur kurz keine Verbindung.
        console.warn('[auth-refresh] refresh failed without auth error:', refreshError);
      }
      return Promise.reject(refreshError);
    }
  }
);

// Helper function to clear auth state and redirect
// FE-K04: Nur noch auth-storage Key (Single Source of Truth)
// HINWEIS: Diese Funktion läuft im Axios-Interceptor — ausserhalb des React-Komponentenbaums.
// Daher ist window.location.href die einzige Möglichkeit für den Redirect (kein React Router verfügbar).
function clearAuthAndRedirect() {
  const session = getAuthSession();
  // Import authStore dynamically to avoid circular dependency
  import('../store/authStore').then(({ useAuthStore }) => {
    if (session.revision !== getAuthSession().revision) return;
    useAuthStore.getState().logout();
    // Redirect nach State-Clear — kein React-Kontext verfügbar (Interceptor),
    // daher window.location.href statt React Router navigate()
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  }).catch(() => {
    if (session.revision !== getAuthSession().revision) return;
    // Fallback: persist-Key clearen wenn dynamic import fehlschlägt (SEC-08)
    sessionStorage.removeItem('auth-storage');
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
  });
}

/**
 * Formatiert ein `details`-Objekt (Feld → Fehlerliste) zu einem lesbaren String.
 * Beispiel: { "support_email": ["Not a valid email address."] }
 *        → "support_email: Not a valid email address."
 */
export const formatValidationDetails = (
  details: Record<string, string[]>
): string => {
  return Object.entries(details)
    .map(([field, messages]) => {
      const msgArray = Array.isArray(messages) ? messages : [messages];
      return `${field}: ${msgArray.join(', ')}`;
    })
    .join('\n');
};

// Generic API error handler
export const handleApiError = (error: AxiosError): string => {
  if (error.response?.data) {
    const errorData = error.response.data as any;

    // Handle field-specific validation details (from AppError.payload or global handler)
    if (errorData.details && typeof errorData.details === 'object') {
      const detailStr = formatValidationDetails(errorData.details);
      const prefix = errorData.message || 'Ungültige Eingabe';
      return `${prefix}: ${detailStr.replace(/\n/g, '; ')}`;
    }

    // Fallback: legacy `errors` key (global ValidationError handler)
    if (errorData.errors && typeof errorData.errors === 'object') {
      const detailStr = formatValidationDetails(errorData.errors);
      const prefix = errorData.message || 'Validierungsfehler';
      return `${prefix}: ${detailStr.replace(/\n/g, '; ')}`;
    }

    // Handle single error message (prefer message over error code)
    if (errorData.message || errorData.error) {
      return errorData.message || errorData.error || 'Ein Fehler ist aufgetreten';
    }
  }

  // Network or other errors
  if (error.message) {
    return error.message;
  }

  return 'Ein unbekannter Fehler ist aufgetreten';
};

/**
 * Erweiterter Error-Typ, der den HTTP-Statuscode aus der API-Response bewahrt.
 * Ermöglicht statusspezifische Fehlerbehandlung in Mutations (z. B. 429 → Warning statt Error).
 */
export class ApiError extends Error {
  status: number | undefined;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Generic API wrapper
export const apiWrapper = async <T>(
  apiCall: () => Promise<AxiosResponse<T>>
): Promise<T> => {
  try {
    const response = await apiCall();
    return response.data;
  } catch (error) {
    const axiosErr = error as AxiosError;
    throw new ApiError(
      handleApiError(axiosErr),
      axiosErr.response?.status
    );
  }
};
